
<div class="w-full max-w-6xl grid gap-0 overflow-hidden rounded-2xl shadow-2xl"
     [class.grid-cols-1]="(isLessThanMD$ | async)"
     [class.grid-cols-2]="!(isLessThanMD$ | async)">

  <!-- Left Side - Image Section -->
  <div class="relative bg-cover bg-center bg-no-repeat min-h-[300px] md:min-h-[600px] flex flex-col justify-between p-8 md:p-12"
       [class.hidden]="(isHandset$ | async)"
       style="background-image: url(/images/solar-panel-signup.png)">

    <!-- Logo -->
    <div>
      <img ngSrc="/logo_noya.png" alt="NOYA" class="h-12 w-auto" height="91" width="177" priority />
    </div>

    <!-- Text Content -->
    <div class="text-white">
      <h1 class="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 leading-tight">
        Réinitialisation<br>de mot de passe
      </h1>
      <p class="text-sm md:text-base opacity-90">
        Suivez les étapes simples pour<br>
        réinitialiser votre mot de passe<br>
        en toute sécurité
      </p>
    </div>
  </div>

  <!-- Right Side - Form Section -->
  <div class="bg-white p-8 md:p-12 flex flex-col justify-center">

    <!-- Mobile Logo -->
    @if (isHandset$ | async) {
      <div class="mb-6">
        <img ngSrc="/logo_noya.png" alt="NOYA" class="h-10 w-auto mx-auto" height="91" width="177" priority />
      </div>
    }

    <!-- Progress Indicator -->
    <div class="flex justify-center mb-8">
      <div class="flex items-center space-x-2">
        @for (step of [1, 2, 3]; track step) {
          <div class="flex items-center">
            <div
              class="w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all"
              [class.bg-primary]="currentStep() >= step"
              [class.text-on-primary]="currentStep() >= step"
              [class.bg-outline]="currentStep() < step"
              [class.text-neutral-variant]="currentStep() < step">
              {{ step }}
            </div>
            @if (step < 3) {
              <div
                class="w-8 h-0.5 transition-all"
                [class.bg-primary]="currentStep() > step"
                [class.bg-outline]="currentStep() <= step">
              </div>
            }
          </div>
        }
      </div>
    </div>

    <!-- Step 1: Email -->
    @if (currentStep() === Step.EMAIL) {
      <div>
        <p class="text-xs md:text-sm text-primary font-semibold tracking-wider uppercase mb-2">
          RÉINITIALISATION
        </p>
        <h2 class="text-2xl md:text-3xl font-bold text-neutral mb-6">
          Mot de passe oublié ?
        </h2>
        <p class="text-sm text-neutral-variant mb-6">
          Entrez votre email pour recevoir un code de vérification
        </p>

        <form [formGroup]="emailForm" (ngSubmit)="requestOtp()" class="space-y-4 mb-6">
          <div>
            <input
              type="email"
              formControlName="email"
              placeholder="Email"
              class="w-full px-4 py-3 border border-outline rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
            />
            @if (emailForm.get('email')?.invalid && emailForm.get('email')?.touched) {
              <p class="text-error text-xs mt-1">Email valide requis</p>
            }
          </div>

          <button
            type="submit"
            [disabled]="!emailForm.valid || loading()"
            class="w-full bg-secondary text-on-secondary py-3 rounded-lg font-semibold hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            @if (loading()) {
              <mat-spinner diameter="20" color="accent"></mat-spinner>
              <span>Envoi...</span>
            } @else {
              <span>Envoyer le code</span>
            }
          </button>
        </form>

        <div class="text-center">
          <button
            (click)="goBack()"
            class="text-secondary text-sm hover:underline"
          >
            Retour à la connexion
          </button>
        </div>
      </div>
    }

    <!-- Step 2: Verify OTP -->
    @if (currentStep() === Step.VERIFY_OTP) {
      <div>
        <p class="text-xs md:text-sm text-primary font-semibold tracking-wider uppercase mb-2">
          VÉRIFICATION
        </p>
        <h2 class="text-2xl md:text-3xl font-bold text-neutral mb-2">
          Code de vérification
        </h2>
        <p class="text-sm text-neutral-variant mb-6">
          Entrez le code à 6 chiffres envoyé à<br>
          <span class="font-semibold text-neutral">{{ email() }}</span>
        </p>

        <div class="mb-6">
          <code-input
            [isCodeHidden]="false"
            [codeLength]="6"
            [initialFocusField]="0"
            (codeCompleted)="onCodeCompleted($event)"
            (codeChanged)="onCodeChanged($event)"
            [code]="otpCode()"
          ></code-input>
        </div>

        <!-- Timer -->
        <div class="text-center mb-4">
          @if (timeRemaining() > 0) {
            <p class="text-sm text-neutral-variant">
              Code expire dans:
              <span class="font-semibold text-primary">
                {{ minutes() }}:{{ seconds() < 10 ? '0' + seconds() : seconds() }}
              </span>
            </p>
          } @else {
            <p class="text-sm text-error">Code expiré</p>
          }
        </div>

        <!-- Resend -->
        <div class="text-center mb-6">
          <button
            (click)="resendOtp()"
            [disabled]="!canResend() || loading()"
            class="text-secondary text-sm font-semibold hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Renvoyer le code
          </button>
        </div>

        <button
          (click)="goBack()"
          class="w-full border-2 border-outline text-neutral py-3 rounded-lg font-semibold hover:bg-surface-container-low transition-all"
        >
          Retour
        </button>
      </div>
    }

    <!-- Step 3: Reset Password -->
    @if (currentStep() === Step.RESET_PASSWORD) {
      <div>
        <p class="text-xs md:text-sm text-primary font-semibold tracking-wider uppercase mb-2">
          NOUVEAU MOT DE PASSE
        </p>
        <h2 class="text-2xl md:text-3xl font-bold text-neutral mb-6">
          Créer un mot de passe
        </h2>

        <form [formGroup]="resetPasswordForm" (ngSubmit)="resetPassword()" class="space-y-4 mb-6">
          <div>
            <input
              type="password"
              formControlName="newPassword"
              placeholder="Nouveau mot de passe"
              class="w-full px-4 py-3 border border-outline rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
            />
            @if (resetPasswordForm.get('newPassword')?.invalid && resetPasswordForm.get('newPassword')?.touched) {
              <p class="text-error text-xs mt-1">
                Minimum 8 caractères avec majuscule, minuscule, chiffre et caractère spécial
              </p>
            }
          </div>

          <div>
            <input
              type="password"
              formControlName="confirmPassword"
              placeholder="Confirmer le mot de passe"
              class="w-full px-4 py-3 border border-outline rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
            />
            @if (resetPasswordForm.get('confirmPassword')?.errors?.['passwordMismatch'] && resetPasswordForm.get('confirmPassword')?.touched) {
              <p class="text-error text-xs mt-1">Les mots de passe ne correspondent pas</p>
            }
          </div>

          <button
            type="submit"
            [disabled]="!resetPasswordForm.valid || loading()"
            class="w-full bg-secondary text-on-secondary py-3 rounded-lg font-semibold hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            @if (loading()) {
              <mat-spinner diameter="20" color="accent"></mat-spinner>
              <span>Réinitialisation...</span>
            } @else {
              <span>Réinitialiser</span>
            }
          </button>
        </form>
        <button
          (click)="goBack()"
          class="w-full border-2 border-outline text-neutral py-3 rounded-lg font-semibold hover:bg-surface-container-low transition-all flex items-center justify-center gap-2"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
          <span>Retour</span>
        </button>
      </div>
    }
  </div>
</div>
